<template>
  <div class="ctx-page">
    <div class="ctx-page-inner">
      <!-- Heading row: title left, brand line right -->
      <div class="ctx-head">
        <div>
          <span class="ctx-eyebrow">Capacio AI</span>
          <h1 class="ctx-title">
            Choose analysis environment
          </h1>
          <p class="ctx-sub">
            Same AI. More perspectives. Choose the environment that fits your needs.
          </p>
        </div>
        <div class="ctx-head-right">
          <p class="ctx-tagline">
            Understand<br>people deeper
          </p>
          <div class="ctx-head-buttons">
            <button
              v-if="current"
              class="ctx-back"
              @click="$emit('close')"
            >
              Back to chat
            </button>
            <!-- Settings gear hidden for now (Oct 2026): open /chatbot/settings directly. -->
            <router-link
              v-if="false"
              class="admin-gear"
              title="Chatbot behaviour settings"
              :to="{ name: 'ChatbotSettings' }"
            >
              <cai-icon
                name="gear"
                :size="16"
              />
            </router-link>
          </div>
        </div>
      </div>

      <!-- One card per environment: general first, then the domains -->
      <div class="ctx-grid">
        <div
          v-for="c in cards"
          :key="c.id"
          class="ctx-card"
          :class="{ 'is-locked': !c.available, 'is-current': c.id === current }"
        >
          <div
            class="ctx-card-banner"
            :class="'ctx-banner--' + c.theme"
          >
            <span class="ctx-badge">{{ c.badge }}</span>
            <!-- Environments we have artwork for use the photo; anything else
                 falls back to a tinted panel with its icon as a watermark. -->
            <img
              v-if="c.image"
              :src="c.image"
              alt=""
              class="ctx-banner-photo"
            >
            <cai-icon
              v-else
              :name="c.icon"
              :size="72"
              class="ctx-banner-art"
            />
          </div>

          <!-- Sits outside the banner so it can overlap its clipped edge -->
          <span class="ctx-card-mark">
            <cai-icon
              :name="c.icon"
              :size="18"
            />
          </span>

          <div class="ctx-card-body">
            <h2 class="ctx-card-name">
              {{ c.name }}
            </h2>
            <p class="ctx-card-kind">
              {{ c.kind }}
            </p>
            <p class="ctx-card-desc">
              {{ c.description }}
            </p>

            <button
              v-if="c.available"
              class="ctx-cta"
              @click="$emit('select', c.raw)"
            >
              {{ c.id === current ? 'Continue in' : 'Open' }} {{ c.name }}
              <cai-icon
                name="arrow"
                :size="14"
              />
            </button>
            <button
              v-else
              class="ctx-cta ctx-cta--locked"
              disabled
              title="This environment is not part of your licence"
            >
              <cai-icon
                name="lock"
                :size="13"
              />
              Request access
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="!cards.length"
        class="ctx-empty"
      >
        No environments available. Check that the chatbot API is reachable.
      </p>

      <!-- How it works + starting questions -->
      <div class="ctx-how">
        <div class="ctx-how-main">
          <h3 class="ctx-how-title">
            How CAI works
          </h3>
          <div class="ctx-steps">
            <div
              v-for="(s, i) in steps"
              :key="s.title"
              class="ctx-step"
            >
              <span class="ctx-step-icon">
                <cai-icon
                  :name="s.icon"
                  :size="17"
                />
              </span>
              <span class="ctx-step-body">
                <span class="ctx-step-title">{{ i + 1 }}. {{ s.title }}</span>
                <span class="ctx-step-desc">{{ s.desc }}</span>
              </span>
              <span
                v-if="i < steps.length - 1"
                class="ctx-step-arrow"
              >›</span>
            </div>
          </div>
        </div>

        <div class="ctx-how-aside">
          <h3 class="ctx-how-title">
            Want to know more?
          </h3>
          <div class="ctx-links">
            <cai-icon
              name="book"
              :size="16"
            />
            <div>
              <button
                v-for="q in questions"
                :key="q.label"
                class="ctx-link"
                @click="ask(q.message)"
              >
                {{ q.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CaiIcon from './CaiIcon.vue'

// Icon by context id, then by parent kind, then a neutral default.
const ICONS = { football: 'football', handball: 'handball', business: 'chart', general: 'brain' }
const THEMES = { football: 'pitch', handball: 'court', general: 'sand' }

// Card artwork, per environment. Contexts without a photo keep the tinted
// fallback panel, so adding a sport from the admin dashboard still looks right.
const IMAGES = {
  general: require('@/assets/capacio/chatbot/env-general.png'),
  football: require('@/assets/capacio/chatbot/env-football.png'),
  handball: require('@/assets/capacio/chatbot/env-handball.png')
}

const STEPS = [
  {
    icon: 'cube-scan',
    title: 'The Capacio core',
    desc: 'Cognitive (executive) measures and psychological characteristics.'
  },
  {
    icon: 'point3d',
    title: 'Domain knowledge',
    desc: 'For example football, handball and other domains with relevant models and evidence.'
  },
  {
    icon: 'team',
    title: 'Your context',
    desc: 'Your specific knowledge, data and terminology — your club or organisation — is added here.'
  }
]

const QUESTIONS = [
  { label: 'Who is CAI?', message: 'Who are you, and what can you help me with?' },
  { label: 'What measures are used?', message: 'Which measures does Capacio use, and what does each one capture?' },
  { label: 'How do we interpret results?', message: 'How should I read a stanine score from 1 to 9?' },
  { label: 'Limitations and ethics', message: 'What are the limitations of these measures, and what should I be careful about ethically?' }
]

export default {
  name: 'ContextPicker',

  components: { CaiIcon },

  props: {
    // [{ id, name, parent, status, description }] from GET /contexts.
    contexts: { type: Array, default: () => [] },
    // The context already in use, if the picker was re-opened to switch.
    current: { type: String, default: '' }
  },

  data () {
    return { steps: STEPS, questions: QUESTIONS }
  },

  computed: {
    // General first, then every other environment — shown as soon as the org
    // exists, regardless of the pack's status. The backend never gates
    // querying on status (it's just an admin-side label), so there's no
    // reason to hide a freshly created org from the picker.
    cards () {
      const ordered = [
        ...this.contexts.filter(c => c.id === 'general'),
        ...this.contexts.filter(c => c.id !== 'general')
      ]
      return ordered.map(c => this.toCard(c))
    }
  },

  methods: {
    toCard (c) {
      const available = true
      const isGeneral = c.id === 'general'
      // Pack names carry a parenthetical ("Football (Soccer)") that reads badly
      // in a title — keep it out of the card headline.
      const label = String(c.name || c.id).replace(/\s*\([^)]*\)\s*$/, '')
      return {
        id: c.id,
        raw: c,
        name: isGeneral ? 'CAI' : `CAI ${label}`,
        kind: isGeneral ? 'Psychological analysis' : `${label}-specific analysis`,
        description: c.description || this.defaultDesc(label, isGeneral),
        icon: ICONS[c.id] || ICONS[c.parent] || 'compass',
        image: IMAGES[c.id] || null,
        theme: THEMES[c.id] || 'sand',
        badge: isGeneral
          ? 'Always available'
          : (available ? 'Available' : 'Not included in your licence'),
        available
      }
    },

    defaultDesc (label, isGeneral) {
      if (isGeneral) {
        return "Analyse individuals, compare profiles and explore patterns in teams and organisations. CAI is based on Capacio's cognitive (executive) measures and self-reported psychological characteristics, and can also interpret other evidence-based psychological test results."
      }
      return `Add ${label.toLowerCase()} roles, situations, demands and performance context to the analysis. Combine psychological profiles with performance, statistics and team context.`
    },

    // The "want to know more" links start a general chat on that question.
    ask (message) {
      const general = this.contexts.find(c => c.id === 'general') || this.contexts[0]
      if (general) this.$emit('ask', { context: general, message })
    }
  }
}
</script>
